import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
  inject,
  input,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

const MIN_THUMB_PX = 24;
const MIN_HORIZONTAL_THUMB_PX = 32;
const SCROLL_IDLE_MS = 700;

export type ScrollRegionOrientation = 'vertical' | 'horizontal' | 'both';

/**
 * Native scrolling viewport with a custom hover-visible scrollbar thumb.
 * Wheel, touch, keyboard, and programmatic scroll remain browser-native.
 */
@Component({
  selector: 'bds-scroll-region',
  standalone: true,
  imports: [NgClass],
  templateUrl: './scroll-region.component.html',
  styleUrl: './scroll-region.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScrollRegionComponent implements AfterViewInit, OnDestroy {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _ngZone = inject(NgZone);

  @ViewChild('viewport', { static: true })
  private readonly _viewportRef!: ElementRef<HTMLElement>;

  @ViewChild('content', { static: true })
  private readonly _contentRef!: ElementRef<HTMLElement>;

  /** Extra classes applied to the native scroll viewport. */
  @Input() styleClass = '';

  /**
   * Scroll axis. `vertical` (default) and `horizontal` are single-axis.
   * `both` keeps one viewport that scrolls on X and Y so a capped table can
   * reach hidden columns without nesting two regions.
   */
  readonly orientation = input<ScrollRegionOrientation>('vertical');

  /** Optional fixed height (e.g. `calc(100vh - 170px)`). */
  @Input() height?: string;

  /**
   * Optional maximum height for content-sized overlays (e.g. `60vh`). The region
   * grows with its content up to this cap, then scrolls. Ignored when `height` is set.
   */
  @Input() maxHeight?: string;

  /** Accessible name for the scrollable region. */
  @Input() ariaLabel = 'Scrollable content';

  readonly overflow = signal(false);
  readonly overflowX = signal(false);
  readonly overflowY = signal(false);
  readonly thumbHeightPx = signal(MIN_THUMB_PX);
  readonly thumbTopPx = signal(0);
  readonly thumbWidthPx = signal(MIN_HORIZONTAL_THUMB_PX);
  readonly thumbLeftPx = signal(0);
  readonly dragging = signal(false);
  readonly scrolling = signal(false);

  @HostBinding('style.height')
  get hostHeight(): string | null {
    return this.isHorizontalMode ? null : (this.height ?? null);
  }

  @HostBinding('class.bds-scroll-region--horizontal')
  get isHorizontalMode(): boolean {
    return this.orientation() === 'horizontal';
  }

  @HostBinding('class.bds-scroll-region--both')
  get isBothMode(): boolean {
    return this.orientation() === 'both';
  }

  /** In max-height mode the host grows to content (capped by `maxHeight`) instead of filling its parent. */
  @HostBinding('class.bds-scroll-region--max-height')
  get isMaxHeightMode(): boolean {
    return !this.isHorizontalMode && !this.height && !!this.maxHeight;
  }

  @HostBinding('style.max-height')
  get hostMaxHeight(): string | null {
    return this.isMaxHeightMode ? (this.maxHeight ?? null) : null;
  }

  private _resizeObserver: ResizeObserver | null = null;
  private _contentObserver: ResizeObserver | null = null;
  private _scrollIdleTimer: ReturnType<typeof setTimeout> | null = null;
  private _scrollRafId: number | null = null;
  private _dragStartY = 0;
  private _dragStartScrollTop = 0;
  private _dragStartX = 0;
  private _dragStartScrollLeft = 0;

  get viewportElement(): HTMLElement {
    return this._viewportRef.nativeElement;
  }

  ngAfterViewInit(): void {
    const viewport = this.viewportElement;

    // Keep high-frequency scroll events outside Angular and coalesce to a frame.
    this._ngZone.runOutsideAngular(() => {
      fromEvent(viewport, 'scroll', { passive: true })
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(() => {
          if (this._scrollRafId != null) {
            return;
          }
          this._scrollRafId = requestAnimationFrame(() => {
            this._scrollRafId = null;
            this._ngZone.run(() => {
              this._markScrolling();
              this._updateThumbMetrics();
            });
          });
        });
    });

    if (typeof ResizeObserver !== 'undefined') {
      const onResize = () =>
        this._ngZone.run(() => this._updateThumbMetrics());
      this._resizeObserver = new ResizeObserver(onResize);
      this._resizeObserver.observe(viewport);

      // Observe aggregate projected content as rows/messages are added or removed.
      this._contentObserver = new ResizeObserver(onResize);
      this._contentObserver.observe(this._contentRef.nativeElement);
    }

    queueMicrotask(() => this._updateThumbMetrics());
  }

  ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
    this._contentObserver?.disconnect();
    if (this._scrollIdleTimer) {
      clearTimeout(this._scrollIdleTimer);
    }
    if (this._scrollRafId != null) {
      cancelAnimationFrame(this._scrollRafId);
      this._scrollRafId = null;
    }
  }

  scrollTo(options: ScrollToOptions): void {
    this.viewportElement.scrollTo(options);
  }

  onThumbPointerDown(event: PointerEvent): void {
    if (this.isHorizontalMode || !this.overflowY()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const thumb = event.currentTarget as HTMLElement;
    try {
      thumb.setPointerCapture(event.pointerId);
    } catch {
      // jsdom / older browsers may not support pointer capture
    }
    this.dragging.set(true);
    this._dragStartY = event.clientY;
    this._dragStartScrollTop = this.viewportElement.scrollTop;
  }

  onThumbPointerMove(event: PointerEvent): void {
    if (this.isHorizontalMode || !this.dragging()) {
      return;
    }
    const viewport = this.viewportElement;
    const maxScroll = viewport.scrollHeight - viewport.clientHeight;
    if (maxScroll <= 0) {
      return;
    }
    const trackTravel = viewport.clientHeight - this.thumbHeightPx();
    if (trackTravel <= 0) {
      return;
    }
    const deltaY = event.clientY - this._dragStartY;
    const scrollDelta = (deltaY / trackTravel) * maxScroll;
    viewport.scrollTop = this._dragStartScrollTop + scrollDelta;
  }

  onThumbPointerUp(event: PointerEvent): void {
    if (this.isHorizontalMode || !this.dragging()) {
      return;
    }
    const thumb = event.currentTarget as HTMLElement;
    try {
      if (thumb.hasPointerCapture?.(event.pointerId)) {
        thumb.releasePointerCapture(event.pointerId);
      }
    } catch {
      // ignore
    }
    this.dragging.set(false);
  }

  onHorizontalThumbPointerDown(event: PointerEvent): void {
    if ((!this.isHorizontalMode && !this.isBothMode) || !this.overflowX()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const thumb = event.currentTarget as HTMLElement;
    try {
      thumb.setPointerCapture(event.pointerId);
    } catch {
      // jsdom / older browsers may not support pointer capture
    }
    this.dragging.set(true);
    this._dragStartX = event.clientX;
    this._dragStartScrollLeft = this.viewportElement.scrollLeft;
  }

  onHorizontalThumbPointerMove(event: PointerEvent): void {
    if ((!this.isHorizontalMode && !this.isBothMode) || !this.dragging()) {
      return;
    }
    const viewport = this.viewportElement;
    const maxScroll = viewport.scrollWidth - viewport.clientWidth;
    if (maxScroll <= 0) {
      return;
    }
    const trackTravel = viewport.clientWidth - this.thumbWidthPx();
    if (trackTravel <= 0) {
      return;
    }
    const deltaX = event.clientX - this._dragStartX;
    const scrollDelta = (deltaX / trackTravel) * maxScroll;
    viewport.scrollLeft = this._dragStartScrollLeft + scrollDelta;
    this._updateThumbMetrics();
  }

  onHorizontalThumbPointerUp(event: PointerEvent): void {
    if ((!this.isHorizontalMode && !this.isBothMode) || !this.dragging()) {
      return;
    }
    const thumb = event.currentTarget as HTMLElement;
    try {
      if (thumb.hasPointerCapture?.(event.pointerId)) {
        thumb.releasePointerCapture(event.pointerId);
      }
    } catch {
      // ignore
    }
    this.dragging.set(false);
  }

  /** Exposed for unit tests. */
  updateThumbMetricsForTest(): void {
    this._updateThumbMetrics();
  }

  private _markScrolling(): void {
    this.scrolling.set(true);
    if (this._scrollIdleTimer) {
      clearTimeout(this._scrollIdleTimer);
    }
    this._scrollIdleTimer = setTimeout(() => {
      this.scrolling.set(false);
      this._scrollIdleTimer = null;
    }, SCROLL_IDLE_MS);
  }

  private _updateThumbMetrics(): void {
    const viewport = this.viewportElement;
    const canScrollX = viewport.scrollWidth > viewport.clientWidth + 1;
    const canScrollY = viewport.scrollHeight > viewport.clientHeight + 1;

    if (this.isHorizontalMode) {
      this.overflowX.set(canScrollX);
      this.overflowY.set(false);
      this.overflow.set(canScrollX);
      this._updateHorizontalThumb(viewport, canScrollX);
      return;
    }

    this.overflowY.set(canScrollY);
    this.overflowX.set(this.isBothMode && canScrollX);
    this.overflow.set(this.isBothMode ? canScrollX || canScrollY : canScrollY);
    this._updateVerticalThumb(viewport, canScrollY);
    if (this.isBothMode) {
      this._updateHorizontalThumb(viewport, canScrollX);
    }
  }

  private _updateHorizontalThumb(
    viewport: HTMLElement,
    canScroll: boolean,
  ): void {
    const { clientWidth, scrollWidth, scrollLeft } = viewport;
    if (!canScroll || clientWidth <= 0) {
      this.thumbWidthPx.set(MIN_HORIZONTAL_THUMB_PX);
      this.thumbLeftPx.set(0);
      return;
    }
    const thumbWidth = Math.max(
      MIN_HORIZONTAL_THUMB_PX,
      Math.round(clientWidth * (clientWidth / scrollWidth)),
    );
    const maxScroll = scrollWidth - clientWidth;
    const maxThumbLeft = clientWidth - thumbWidth;
    this.thumbWidthPx.set(thumbWidth);
    this.thumbLeftPx.set(Math.round((scrollLeft / maxScroll) * maxThumbLeft));
  }

  private _updateVerticalThumb(
    viewport: HTMLElement,
    canScroll: boolean,
  ): void {
    const { clientHeight, scrollHeight, scrollTop } = viewport;
    if (!canScroll) {
      this.thumbHeightPx.set(MIN_THUMB_PX);
      this.thumbTopPx.set(0);
      return;
    }
    const thumbHeight = Math.max(
      MIN_THUMB_PX,
      Math.round(clientHeight * (clientHeight / scrollHeight)),
    );
    const maxScroll = scrollHeight - clientHeight;
    const maxThumbTop = clientHeight - thumbHeight;
    this.thumbHeightPx.set(thumbHeight);
    this.thumbTopPx.set(Math.round((scrollTop / maxScroll) * maxThumbTop));
  }
}
